import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { ActionStatus, JobAction } from '../../action.types';
import { AlertDialog } from '@intranet/shared/alert/alert-dialog.component';
import { PlannedBlockService } from './planned-block.service';
import { PlannerCacheService } from './planner-cache.service';

// Spec, "Action Status Changes": when an Action is Completed or Archived,
// past and today's blocks are left untouched (so planned vs actual can be
// compared later), and if it has future blocks the user is prompted to
// remove them.
//
// Called from ActionService.update after every successful save, so it
// covers every place an action's status can change (list, board, bulk
// edit, the action form, finance, snapshots) without each needing its own
// hook. Saves made close together (a bulk status change) are gathered into
// one prompt rather than one per action.
//
// Deleting an action asks the same question first (askBeforeDelete) — the
// server keeps its past and today's blocks, and removes the future ones too
// only if the user chose to.
//
// Only blocks the user may remove are counted and removed: their own, or
// anyone's with the Planner role (the server applies the same rule).
interface PendingAction {
    title: string;
    archived: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class PlannedBlockStatusPromptService {
    private static readonly GATHER_MS = 400;

    // Completed/Archived saves waiting to be checked.
    private _pending = new Map<number, PendingAction>();
    private _gatherTimer: ReturnType<typeof setTimeout> | null = null;
    // Actions the user chose to keep future blocks for — not asked again on
    // later saves this session (e.g. editing a completed action's notes).
    // Cleared again if the action is reopened.
    private _keptFutureBlocks = new Set<number>();

    constructor(
        private _dialog: MatDialog,
        private _plannedBlockService: PlannedBlockService,
        private _plannerCache: PlannerCacheService
    ) {}

    onActionSaved(action: JobAction | null | undefined): void {
        if (!action?.jobActionId) return;
        const finished = action.actionStatus === ActionStatus.Completed
            || action.actionStatus === ActionStatus.Archived;
        if (!finished) {
            this._keptFutureBlocks.delete(action.jobActionId);
            return;
        }
        if (this._keptFutureBlocks.has(action.jobActionId)) return;

        this._pending.set(action.jobActionId, {
            title: action.title || '',
            archived: action.actionStatus === ActionStatus.Archived,
        });
        if (this._gatherTimer) clearTimeout(this._gatherTimer);
        this._gatherTimer = setTimeout(() => this.checkPending(), PlannedBlockStatusPromptService.GATHER_MS);
    }

    private checkPending(): void {
        this._gatherTimer = null;
        const actions = new Map(this._pending);
        this._pending.clear();
        const ids = Array.from(actions.keys());
        if (!ids.length) return;

        // "Future" = from the start of tomorrow, in this user's own time —
        // today's blocks stay, per the spec.
        const from = new Date();
        from.setHours(24, 0, 0, 0);

        this._plannedBlockService.getFutureBlockCount(ids, from).subscribe({
            next: (count) => {
                if (count > 0) this.ask(ids, actions, count, from);
            },
            // Best-effort — a failed check just means no prompt, never a
            // failed status change.
            error: () => {},
        });
    }

    // Wording aims to say exactly what each button does: which action, how
    // many blocks, that only blocks after today are affected, and that
    // today's and earlier ones always stay.
    private ask(ids: number[], actions: Map<number, PendingAction>, count: number, from: Date): void {
        const blocks = count === 1 ? '1 planned block' : `${count} planned blocks`;
        const them = count === 1 ? 'it' : 'them';
        let lead: string;
        if (ids.length === 1) {
            const action = actions.get(ids[0]);
            const title = action?.title || '';
            const name = title ? `"${title.charAt(0).toUpperCase()}${title.slice(1)}"` : 'This action';
            const status = action?.archived ? 'archived' : 'completed';
            lead = `The action ${name} is now ${status}, but it still has ${blocks} on the planner after today.`;
        } else {
            lead = `The ${ids.length} actions you've just completed or archived still have ${blocks} on the planner after today.`;
        }

        // Button labels kept short enough to stay on one line each side by
        // side; the message above carries the detail (which action, how
        // many blocks).
        // 640px fits the title and each button label on one line on
        // desktop; narrower screens still shrink it to fit.
        const dialogRef = this._dialog.open(AlertDialog, {
            width: '640px',
            maxWidth: '92vw',
            panelClass: 'PlannerFutureBlocksDialog', // spacing — see vendor/_material.scss
            data: {
                title: count === 1 ? 'Remove the future planned block?' : 'Remove the future planned blocks?',
                message: `${lead} Would you like to remove ${them}? Planned blocks for today and earlier days always stay on the planner.`,
                confirm: true,
                confirmTitle: count === 1 ? 'Remove block after today' : 'Remove blocks after today',
                cancelTitle: count === 1 ? 'Keep the block' : 'Keep all blocks',
            },
        });

        dialogRef.afterClosed().subscribe((remove) => {
            if (!remove) {
                ids.forEach((id) => this._keptFutureBlocks.add(id));
                return;
            }
            this._plannedBlockService.deleteFutureBlocks(ids, from).subscribe({
                // Blocks can sit on anyone's planner — drop every cached
                // copy, and let an open Planner re-render.
                next: () => this._plannerCache.invalidateAllBlocks(),
                error: () => alert('Could not remove the planned blocks. Please try again.'),
            });
        });
    }

    /** Before an action is deleted (after the user has confirmed the delete
     *  itself): if it has planned blocks after today, asks whether to remove
     *  them too. Emits the moment from which to remove them (the start of
     *  tomorrow, in the user's own time) if so, or null to keep them —
     *  today's and earlier blocks always stay (spec, Action Status Changes).
     *  Never fails: if the check can't be made, nothing extra is removed. */
    askBeforeDelete(action: JobAction): Observable<Date | null> {
        if (!action?.jobActionId) return of(null);
        const from = new Date();
        from.setHours(24, 0, 0, 0);
        return this._plannedBlockService.getFutureBlockCount([action.jobActionId], from).pipe(
            catchError(() => of(0)),
            switchMap((count) => {
                if (!count) return of(null);
                const blocks = count === 1 ? '1 planned block' : `${count} planned blocks`;
                const them = count === 1 ? 'it' : 'them';
                const title = action.title || '';
                const name = title ? `"${title.charAt(0).toUpperCase()}${title.slice(1)}"` : 'This action';
                const dialogRef = this._dialog.open(AlertDialog, {
                    width: '640px',
                    maxWidth: '92vw',
                    panelClass: 'PlannerFutureBlocksDialog', // spacing — see vendor/_material.scss
                    data: {
                        title: count === 1 ? 'Remove the future planned block?' : 'Remove the future planned blocks?',
                        message: `The action ${name} is being deleted, but it still has ${blocks} on the planner after today. Would you like to remove ${them} as well? Planned blocks for today and earlier days always stay on the planner.`,
                        confirm: true,
                        confirmTitle: count === 1 ? 'Remove block after today' : 'Remove blocks after today',
                        cancelTitle: count === 1 ? 'Keep the block' : 'Keep all blocks',
                    },
                });
                // Anything but "Remove" (Keep, Escape, a click outside) keeps them.
                return dialogRef.afterClosed().pipe(map((remove) => (remove ? from : null)));
            })
        );
    }

    /** After an action has been deleted: its kept blocks now show its saved
     *  details, so cached copies (on anyone's planner) are refreshed. */
    onActionDeleted(): void {
        this._plannerCache.invalidateAllBlocks();
    }
}
